import { RectNode, RectNodeModel, h } from '@logicflow/core';
import { drawLabel } from './textUtils';
import { applySimStatusStyle } from './simStatus';
import { getThemeStyle } from '../theme';

function bkmBadge(h, x, y, width, height) {
  // Mini BKM-pentagon in the top-right corner of the Decision rectangle.
  const bw = 16;
  const bh = 12;
  const cut = 4;
  const l = x + width / 2 - bw - 6;
  const t = y - height / 2 + 6;
  const r = l + bw;
  const b = t + bh;
  const d = `M ${l + cut} ${t} L ${r} ${t} L ${r} ${b - cut} L ${r - cut} ${b} L ${l} ${b} L ${l} ${t + cut} Z`;
  return h('path', {
    d,
    fill: '#f9f0ff',
    stroke: '#722ed1',
    strokeWidth: 1.2,
  });
}

// Pill in the top-left corner that reflects the Decision's current logic type
// (Table, Literal, Context, …) so the user can see the mode at a glance from
// the DRD without opening the editor.
const LOGIC_BADGE = {
  'dmn:DecisionTable':     { sym: '▦',  fill: '#e6fffb', stroke: '#13c2c2' },
  'dmn:LiteralExpression': { sym: '=',  fill: '#fff1f0', stroke: '#cf1322' },
  'dmn:Context':           { sym: '{}', fill: '#fff7e6', stroke: '#d46b08' },
  'dmn:List':              { sym: '[]', fill: '#f6ffed', stroke: '#52c41a' },
  'dmn:Invocation':        { sym: 'ƒ()',fill: '#f9f0ff', stroke: '#722ed1' },
  'dmn:Relation':          { sym: '⚏',  fill: '#e6f7ff', stroke: '#1890ff' },
  'dmn:FunctionDefinition':{ sym: 'λ',  fill: '#feffe6', stroke: '#7cb305' },
  // EPX extension — non-standard logic types live here. Magenta singles them
  // out from the seven standard DMN boxed-expression badges above.
  'epx:MachineLearningDecision': { sym: 'ML', fill: '#fff0f6', stroke: '#c41d7f' },
};

function logicTypeBadge(h, x, y, width, height, logicType) {
  const info = LOGIC_BADGE[logicType];
  if (!info) return null;
  const bw = 24;
  const bh = 14;
  const l = x - width / 2 + 6;
  const t = y - height / 2 + 6;
  return h('g', {}, [
    h('rect', {
      x: l, y: t, width: bw, height: bh, rx: 3, ry: 3,
      fill: info.fill, stroke: info.stroke, strokeWidth: 1,
    }),
    h('text', {
      x: l + bw / 2,
      y: t + bh / 2 + 3,
      textAnchor: 'middle',
      fontSize: 9,
      fontWeight: 700,
      fill: info.stroke,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      style: 'user-select:none;pointer-events:none;',
    }, info.sym),
  ]);
}

class DecisionView extends RectNode {
  getShape() {
    const { model } = this.props;
    const { x, y, width, height } = model;
    const style = model.getNodeStyle();
    const label = model.properties?.name || model.text?.value || '';
    const hasBkm = !!model.properties?.hasBkmConnected;
    const logicType = model.properties?.logicType;
    const textFill = (model.graphModel?.__epxDmnTheme?.text?.fill) || '#212121';

    const children = [
      h('rect', {
        x: x - width / 2,
        y: y - height / 2,
        width,
        height,
        ...style,
      }),
      drawLabel(h, label, x, y, width - 16, { fill: textFill }),
    ];
    if (hasBkm) children.push(bkmBadge(h, x, y, width, height));
    const logicNode = logicTypeBadge(h, x, y, width, height, logicType);
    if (logicNode) children.push(logicNode);

    return h('g', {}, children);
  }

  getText() { return ''; }
}

class DecisionModel extends RectNodeModel {
  initNodeData(data) {
    super.initNodeData(data);
    this.width = 180;
    this.height = 80;
    this.radius = 0;
    this.text = { ...this.text, editable: true, draggable: false, value: this.text?.value || '' };
  }

  getNodeStyle() {
    const style = super.getNodeStyle();
    const t = getThemeStyle(this.graphModel, 'decision');
    style.stroke = t.stroke || '#1890ff';
    style.strokeWidth = t.strokeWidth || 2;
    style.fill = t.fill || '#e6f7ff';
    return applySimStatusStyle(style, this.properties);
  }
}

export const Decision = {
  type: 'dmn:Decision',
  view: DecisionView,
  model: DecisionModel,
};
